// useACISuggestions — react-query hook for ACI suggestions per surface.
// Established CHG-1274 (v11). Behavior updated CHG-1322 (v14): fetchACISuggestions
// now throws on 5xx/network errors (404 stays soft-empty); the `error`
// field returned here drives ACISurface's "Suggestions unavailable" error
// branch (added v14).
//
// Configuration:
//   - retry: false       — failure modes are either 404 (soft-empty, no
//                          retry value) or 5xx/network (caller decides via
//                          error state); silent retries would mask outages.
//   - staleTime: 60_000  — 1 minute. ACI suggestions are advisory + slow-
//                          moving (seasonal, lifecycle); refetching every
//                          render is wasted bandwidth.
//   - useQuery key:      ["aci", surface_id, entity_id, limit] so each
//                          surface caches independently.

import { useQuery } from "@tanstack/react-query";
import { fetchACISuggestions, type ACISuggestion, type ACISurfaceId } from "../../api/aci";

export interface UseACISuggestionsParams {
  surface_id: ACISurfaceId;
  /** Optional entity context (campaign_id / donor_id / recurring_id / pledge_id). */
  entity_id?: string;
  /** Optional org context. When omitted, backend infers from auth. */
  org_id?: string;
  /** Default 5; max 10. */
  limit?: number;
  /** Disable the query (e.g., when entity_id isn't known yet). */
  enabled?: boolean;
}

export interface UseACISuggestionsResult {
  suggestions: ACISuggestion[];
  isLoading: boolean;
  error: Error | null;
}

export function useACISuggestions(params: UseACISuggestionsParams): UseACISuggestionsResult {
  const enabled = params.enabled !== false;
  const { data, isLoading, error } = useQuery({
    queryKey: ["aci", params.surface_id, params.entity_id ?? null, params.limit ?? null],
    queryFn: () =>
      fetchACISuggestions({
        surface_id: params.surface_id,
        entity_id: params.entity_id,
        org_id: params.org_id,
        limit: params.limit,
      }),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
  return {
    suggestions: data ?? [],
    isLoading,
    error: error as Error | null,
  };
}
